import React, { useEffect, useState } from "react";
import { Typography, Descriptions, Card, Modal, Form, Input, Select, Button, Row, Col, DatePicker, message, Divider } from "antd";
import { CopyOutlined } from "@ant-design/icons";

import dayjs from "dayjs";
import * as participantesService from "../../../services/participantes/participantesService";
import * as ubicacionesService from "../../../services/participantes/ubicacionesService";
import "./ParticipantModal.css";

import logosConvenio from '../../../assets/Logo_Prosperidad_Social.png';



const { Option } = Select;

export function ParticipantModal({ open, onCancel, participant, mode, onSaved, userInfo, maestros }) {

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);

  const findIdByCodigo = (maestroArray, codigo) => {
    if (!maestroArray || !codigo) return null;
    const item = maestroArray.find(item => item.codigo === codigo);
    return item ? item.id : null;
  };

  useEffect(() => {
    if (!open) return;

    const loadMunicipios = async () => {
      if ((isEditMode || isViewMode) && participant?.departamento_id) {
        setLoadingMunicipios(true);
        try {
          const munRes = await ubicacionesService.getMunicipios(
            participant.departamento_id,
            1
          );
          setMunicipios(munRes.data || []);
        } catch (error) {
          console.error("Error cargando municipios:", error);
          setMunicipios([]);
        } finally {
          setLoadingMunicipios(false);
        }
      } else {
        setMunicipios([]);
      }
    };
    loadMunicipios();
  }, [open, participant, isEditMode, isViewMode]);




  useEffect(() => {
    if (!open) {
      form.resetFields();
      setMunicipios([]);
      return;
    }

    if (!participant) {
      if (isCreateMode) {
        form.setFieldsValue({
          estado_id: 1,
          tipo_doc_id: 1,
          genero_id: 1
        });
      }
      return;
    }

    const tipoDocId = participant.tipo_documento_id ||
      findIdByCodigo(maestros.tiposDocumento, participant.tipo_documento);

    const generoId = participant.genero_id ||
      findIdByCodigo(maestros.generos, participant.genero);

    const estadoId = participant.estado_participante_id ||
      findIdByCodigo(maestros.estadosParticipante, participant.estado_participante) ||
      1;

    form.setFieldsValue({
      nombres: participant.nombres,
      apellidos: participant.apellidos,
      tipo_doc_id: tipoDocId,
      num_identificacion: participant.num_identificacion,
      genero_id: generoId,
      fecha_nacimiento: participant.fecha_nacimiento ? dayjs(participant.fecha_nacimiento) : null,
      telefono: participant.telefono || "",
      correo: participant.correo || "",
      departamento_id: participant.departamento_id,
      municipio_id: participant.municipio_id,
      estado_id: estadoId,
      direccion: participant.direccion,
      direccion_actual_ruv: participant.direccion_actual_ruv
    });

  }, [open, participant, form, isCreateMode, maestros]);

  const handleDepartamentoChange = async (departamentoId) => {
    if (!departamentoId) {
      setMunicipios([]);
      form.setFieldsValue({ municipio_id: null });
      return;
    }
    setLoadingMunicipios(true);
    try {
      const res = await ubicacionesService.getMunicipios(departamentoId, 1);
      setMunicipios(res.data || []);
      form.setFieldsValue({ municipio_id: null });
    } catch (error) {
      console.error("Error cargando municipios:", error);
      message.error("Error cargando municipios");
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        fecha_nacimiento: values.fecha_nacimiento
          ? values.fecha_nacimiento.format('YYYY-MM-DD')
          : null
      };
      if (isCreateMode) {
        await participantesService.createParticipante(formattedValues, userInfo);
        message.success("Participante creado correctamente");
      } else {
        await participantesService.updateParticipante(
          participant.id,
          formattedValues,
          userInfo
        );
        message.success("Participante actualizado correctamente");
      }
      onSaved(true, participant?.id);
      onCancel();
    } catch (error) {
      console.error("Error guardando participante:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error al guardar los datos";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      style={{ top: 24 }}   // 🔥 ahora sí funciona
      rootClassName="participant-modal"
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
    >
      {/* ===================== */}
      {/* HEADER PERSONALIZADO */}
      {/* ===================== */}
      <Row
        align="middle"
        justify="space-between"
        style={{
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {/* TÍTULO */}
        <Col>
          <Typography.Title
            level={4}
            style={{
              margin: 0,
              color: "#042956",
              fontWeight: 700,
            }}
          >
            {isCreateMode
              ? "Nuevo participante"
              : isEditMode
                ? "Editar participante"
                : "Detalles del participante"}
          </Typography.Title>
        </Col>

        {/* LOGO */}
        <Col>
          <img
            src={logosConvenio}
            alt="Prosperidad Social | Bodecom"
            style={{
              height: 60,
              objectFit: 'contain',
            }}
          />

        </Col>
      </Row>
      {isViewMode ? (

        <div className="view-mode">

          {/* ===================== */}
          {/* INFORMACIÓN PERSONAL */}
          {/* ===================== */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Divider orientation="left" style={{ marginTop: 0 }}>
              Información Personal
            </Divider>

            <Descriptions
              size="small"
              column={{
                xs: 1,
                sm: 1,
                md: 2,
                lg: 3,
              }}
              bordered
            >
              <Descriptions.Item label="Nombres">
                {participant?.nombres || "No especificado"}
              </Descriptions.Item>

              <Descriptions.Item label="Apellidos">
                {participant?.apellidos || "No especificado"}
              </Descriptions.Item>

              <Descriptions.Item label="Género">
                {participant?.genero_nombre || participant?.genero || "No especificado"}
              </Descriptions.Item>

              <Descriptions.Item label="Tipo Documento">
                {participant?.tipo_documento_nombre || participant?.tipo_documento}
              </Descriptions.Item>

              <Descriptions.Item label="Número Identificación">
                {participant?.num_identificacion}
              </Descriptions.Item>

              <Descriptions.Item label="Fecha Nacimiento">
                {participant?.fecha_nacimiento_formateada || participant?.fecha_nacimiento}
              </Descriptions.Item>

              <Descriptions.Item label="Teléfono" span={3}>
                {participant?.telefono ? (
                  <Typography.Text
                    copyable={{
                      text: participant.telefono,
                      tooltips: ["Copiar", "Copiado"],
                      icon: [
                        <CopyOutlined key="copy" />,
                        <CopyOutlined key="copied" />
                      ],
                      onCopy: () => {
                        message.success("Número copiado al portapapeles");
                      }
                    }}
                  >
                    {participant.telefono}
                  </Typography.Text>
                ) : (
                  "No especificado"
                )}
              </Descriptions.Item>

            </Descriptions>
          </Card>

          {/* ===================== */}
          {/* UBICACIÓN */}
          {/* ===================== */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Divider orientation="left" style={{ marginTop: 0 }}>
              Ubicación
            </Divider>

            <Descriptions
              size="small"
              column={{
                xs: 1,
                sm: 1,
                md: 2,
                lg: 3,
              }}
              bordered
            >
              <Descriptions.Item label="Departamento">
                {participant?.departamento}
              </Descriptions.Item>

              <Descriptions.Item label="Municipio">
                {participant?.municipio}
              </Descriptions.Item>

              <Descriptions.Item label="Barrio">
                {participant?.nombre_barrio}
              </Descriptions.Item>

              <Descriptions.Item label="Corregimiento">
                {participant?.nombre_corregimiento}
              </Descriptions.Item>

              <Descriptions.Item label="Vereda">
                {participant?.nombre_vereda}
              </Descriptions.Item>

              <Descriptions.Item label="Dirección registrada" span={3}>
                {participant?.direccion}
              </Descriptions.Item>

              <Descriptions.Item label="Dirección actual" span={3}>
                {participant?.direccion_actual_ruv || "No reportado"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* ===================== */}
          {/* CONTACTO */}
          {/* ===================== */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Divider orientation="left" style={{ marginTop: 0 }}>
              Contacto
            </Divider>

            <Descriptions
              size="small"
              column={{
                xs: 1,
                sm: 1,
                md: 2,
              }}
              bordered
            >
              <Descriptions.Item label="Correo">
                {participant?.correo || "No especificado"}
              </Descriptions.Item>

              <Descriptions.Item label="Correo actual">
                {participant?.correo_act_ruv || "No reportado"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* ===================== */}
          {/* SISTEMA */}
          {/* ===================== */}
          {participant?.id_registro && (
            <Card size="small">
              <Divider orientation="left" style={{ marginTop: 0 }}>
                Sistema
              </Divider>

              <Descriptions
                size="small"
                column={{
                  xs: 1,
                  sm: 1,
                  md: 2,
                }}
                bordered
              >
                <Descriptions.Item label="Código Único">
                  {participant?.id_registro}
                </Descriptions.Item>

                <Descriptions.Item label="Fecha Creación">
                  {participant?.created_at}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* ===================== */}
          {/* ACCIONES */}
          {/* ===================== */}
          <Row justify="end" style={{ marginTop: 24 }}>
            <Button type="primary" onClick={onCancel}>Cerrar</Button>
          </Row>
        </div>

      ) : (
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >

          {/* ===================== */}
          {/* INFORMACIÓN PERSONAL */}
          {/* ===================== */}

          <Card size="small" style={{ marginBottom: 16 }}>
            <Divider orientation="left">Información Personal</Divider>

            <Descriptions
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 3 }}
              bordered
            >

              <Descriptions.Item label="Nombres">
                <Form.Item name="nombres" noStyle>
                  <Input disabled />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Apellidos">
                <Form.Item name="apellidos" noStyle>
                  <Input disabled />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Género">
                <Form.Item name="genero_id" noStyle>
                  <Select disabled>
                    {maestros.generos?.map(g => (
                      <Option key={g.id} value={g.id}>{g.nombre}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Tipo Documento">
                <Form.Item name="tipo_doc_id" noStyle>
                  <Select disabled>
                    {maestros.tiposDocumento?.map(t => (
                      <Option key={t.id} value={t.id}>{t.nombre}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Número Identificación">
                <Form.Item name="num_identificacion" noStyle>
                  <Input disabled />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Fecha Nacimiento">
                <Form.Item name="fecha_nacimiento" noStyle>
                  <DatePicker style={{ width: '100%' }} disabled />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Teléfono" span={3}>
                <Form.Item name="telefono" noStyle>
                  <Input />
                </Form.Item>
              </Descriptions.Item>

            </Descriptions>
          </Card>

          {/* ===================== */}
          {/* UBICACIÓN */}
          {/* ===================== */}

          <Card size="small" style={{ marginBottom: 16 }}>
            <Divider orientation="left">Ubicación</Divider>

            <Descriptions
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 3 }}
              bordered
            >

              <Descriptions.Item label="Departamento">
                <Form.Item name="departamento_id" noStyle>
                  <Select disabled>
                    {maestros.departamentos?.map(d => (
                      <Option key={d.oid} value={d.oid}>{d.departamento}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Municipio">
                <Form.Item name="municipio_id" noStyle>
                  <Select disabled>
                    {municipios.map(m => (
                      <Option key={m.oid} value={m.oid}>{m.municipio}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Dirección registrada" span={3}>
                <Form.Item name="direccion" noStyle>
                  <Input />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label="Dirección actual" span={3}>
                <Form.Item name="direccion_actual_ruv" noStyle>
                  <Input />
                </Form.Item>
              </Descriptions.Item>

            </Descriptions>
          </Card>

          {/* ===================== */}
          {/* CONTACTO */}
          {/* ===================== */}

          <Card size="small">
            <Divider orientation="left">Contacto</Divider>

            <Descriptions
              size="small"
              column={{ xs: 1, sm: 1, md: 2 }}
              bordered
            >

              <Descriptions.Item label="Correo">
                <Form.Item name="correo" noStyle>
                  <Input />
                </Form.Item>
              </Descriptions.Item>

            </Descriptions>
          </Card>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Button onClick={onCancel}>Cancelar</Button>
            <Button type="primary" htmlType="submit" style={{ marginLeft: 8 }}>
              Guardar cambios
            </Button>
          </Row>

        </Form>

      )}
    </Modal>
  );
}